import { useMemo } from "react";
import useModule from "./useModule";
import { Field } from "../types/TenantInterfaces";

// ------- Hook Description ------
// `useFieldMap` creates basic module field maps for easier development experience
// Inside our repo we often store a Field's APIName alone.
// But when we need other values inside a Field's schema, we have to to do lookups
// These lookups become highly repetitive across the codebase
// `useFieldMap` allows you create field maps for instant access to values with one line of code.

// ------ PROPS ------
// `moduleAPIName` is the module api name that stores the fields you want to map.
// `mapType` is the value you want mapped with field.APIName. If no type is provided, it will default to the entire field object.
// EXAMPLES:
// mapType --> FieldType = { [field.APIName]: field.FieldType }
// mapType --> Name = { [field.APIName] : field.Name }
// mapType --> "" = { [field.APIName] : field }
const useFieldMap = (
  moduleAPIName?: string,
  mapType = "",
  separateName = false, // Whether or not we should place Subitem Name in front of value for subitems.
  multiSubitemFieldAPIName?: string,
  customModule?: any
) => {
  const { module: foundModule, modulesLoading } = useModule(moduleAPIName);

  const module = customModule || foundModule;

  const fieldMap = useMemo(() => {
    if (modulesLoading || !module?.Fields?.length) {
      return {} as { [key: string]: any };
    }

    return generateFieldMap(
      module?.Fields,
      "APIName",
      mapType,
      separateName,
      multiSubitemFieldAPIName
    );
  }, [module?.Fields, mapType, separateName, multiSubitemFieldAPIName]);

  return fieldMap;
};

export default useFieldMap;

export const generateFieldMap = (
  fields: Field[],
  key: "APIName",
  value: string,
  separateName?: boolean,
  multiSubitemFieldAPIName?: string
) => {
  const map: { [key: string]: any } = {};

  if (!fields) return map;

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];

    if (multiSubitemFieldAPIName) {
      if (field.APIName === multiSubitemFieldAPIName) {
        for (const multiSubitemField of (field as any).SubitemFields) {
          map[multiSubitemField[key]] = value
            ? multiSubitemField[value]
            : multiSubitemField;
        }
      }
    } else {
      map[field[key]] = value ? (field as any)[value] : field;

      // Handle SubitemFields
      if (!!field.SubitemFields) {
        for (let j = 0; j < field.SubitemFields.length; j++) {
          const subitemField: any = field.SubitemFields[j];

          const subitemKey = `${field[key]}.${subitemField[key]}`;

          if (!value && separateName) {
            const newSubitemField = {
              ...subitemField,
              Name: `${field.Name} - ${subitemField.Name}`,
              APIName: `${field.APIName}.${subitemField.APIName}`,
            };

            map[subitemKey] = newSubitemField;
          } else {
            map[subitemKey] = value
              ? separateName
                ? `${field.Name} - ${subitemField[value]}`
                : subitemField[value]
              : subitemField;
          }
        }
      }
    }
  }

  return map;
};
